import { useLayoutEffect, useRef, useState } from "react";

const PX = 3.7795;
const OVERRIDES = { dob: "Date of Birth", roll_no: "Roll No.", admission_no: "Admission No.", class: "Class & Section", student_photo: "Student Photo", logo: "School Logo", qr_code: "QR Code", attendance_qr: "Attendance QR", principal_signature: "Principal Signature" };
const SAMPLE = { student_name: "Aarav Sharma", first_name: "Aarav", last_name: "Sharma", admission_no: "ADM-1024", roll_no: "12", class: "Grade 5", section: "A", dob: "05-01-2015", gender: "Male", blood_group: "O+", category: "General", nationality: "Indian", house: "Blue House", address: "45 Green Park", city: "Delhi", state: "Delhi", student_phone: "9876500000", father_name: "Rohit Sharma", father_phone: "9876500001", mother_name: "Priya Sharma", mother_phone: "9876500002", guardian_name: "Rohit Sharma", academic_session: "2026-27", class_teacher: "Mrs. Nisha Verma", school_name: "SSS Public School", school_address: "123 School Lane, City", school_phone: "011-2345678", school_email: "info@sssps.edu", udise: "09010100101", school_code: "SSS001", affiliation_no: "AFF123456", principal_name: "Dr. S. K. Rao", website: "www.sssps.edu", tag_line: "Knowledge · Discipline · Excellence", total_fees: "45,000", amount_paid: "30,000", balance_due: "15,000", exam_name: "Half Yearly Examination", exam_term: "Term 1", exam_class: "Grade 5", result_status: "Pass", rank: "3", class_size: "35", percentage: "82.3%", total_obtained: "247", total_max: "300", overall_grade: "A2", subject_1_name: "English", subject_1_max: "100", subject_1_marks: "82", subject_1_grade: "A2", subject_1_result: "Pass", subject_1_date: "15-07-2026", subject_1_day: "Wednesday", subject_1_timing: "09:00 - 11:00", subject_2_name: "Mathematics", subject_2_max: "100", subject_2_marks: "91", subject_2_grade: "A1", subject_2_result: "Pass", subject_2_date: "17-07-2026", subject_2_day: "Friday", subject_2_timing: "09:00 - 11:30", subject_3_name: "Science", subject_3_max: "100", subject_3_marks: "74", subject_3_grade: "B1", subject_3_result: "Pass", subject_3_date: "19-07-2026", subject_3_day: "Sunday", subject_3_timing: "09:00 - 11:00", current_date: new Date().toLocaleDateString("en-GB"), current_year: String(new Date().getFullYear()), issue_date: new Date().toLocaleDateString("en-GB") };
const humanize = (t) => OVERRIDES[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const QR_SRC = (data) => `${process.env.REACT_APP_BACKEND_URL}/api/qr?data=${encodeURIComponent(data)}`;
const EFFECT = (el) => {
  const c = el.effectColor || "#000000";
  if (el.effect === "shadow") return `2px 2px 3px ${c}66`;
  if (el.effect === "outline") return `-1px -1px 0 ${c},1px -1px 0 ${c},-1px 1px 0 ${c},1px 1px 0 ${c}`;
  return "none";
};

function renderEl(el) {
  const common = { position: "absolute", left: el.x, top: el.y, width: el.w, height: el.h, boxSizing: "border-box" };
  if (el.type === "text") {
    const val = el.field ? (SAMPLE[el.field] ?? `{${humanize(el.field)}}`) : el.text;
    return (
      <div key={el.id} style={{ ...common, display: "flex", alignItems: "center", justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start", fontSize: el.fontSize, color: el.color, fontWeight: el.weight || (el.bold ? 700 : 400), fontStyle: el.italic ? "italic" : "normal", textDecoration: el.underline ? "underline" : "none", fontFamily: el.fontFamily, textAlign: el.align, letterSpacing: `${el.letterSpacing || 0}px`, textShadow: EFFECT(el), lineHeight: 1.2, overflow: "hidden", padding: "0 2px" }}>
        {val}
      </div>
    );
  }
  if (el.type === "rect") return <div key={el.id} style={{ ...common, background: el.fill, border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : "none", borderRadius: el.radius }} />;
  if (el.type === "circle") return <div key={el.id} style={{ ...common, background: el.fill, border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : "none", borderRadius: "50%" }} />;
  if (el.type === "line") return <div key={el.id} style={{ ...common, background: el.fill }} />;
  if (el.type === "table") {
    if (el.dynamic) {
      const marks = el.dynamic === "marks";
      const headers = marks ? ["Subject", "Max", "Marks", "Grade", "Result"] : ["Subject", "Date", "Day", "Timing"];
      const sample = marks
        ? [["English", "100", "82", "A2", "Pass"], ["Mathematics", "100", "91", "A1", "Pass"], ["Science", "100", "74", "B1", "Pass"]]
        : [["English", "15-07-2026", "Wednesday", "09:00 - 11:00"], ["Mathematics", "17-07-2026", "Friday", "09:00 - 11:30"], ["Science", "19-07-2026", "Sunday", "09:00 - 11:00"]];
      const bd = el.border ? "1px solid #334155" : "1px solid transparent";
      return (
        <div key={el.id} style={common}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: el.fontSize || 11 }}>
            <tbody>
              <tr>{headers.map((h) => <td key={h} style={{ border: bd, padding: 3, fontWeight: 700, background: el.headerBg || "#e2e8f0" }}>{h}</td>)}</tr>
              {sample.map((row, ri) => <tr key={ri}>{row.map((c, ci) => <td key={ci} style={{ border: bd, padding: 3 }}>{c}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div key={el.id} style={common}>
        <table style={{ width: "100%", height: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            {Array.from({ length: el.rows }).map((_, r) => (
              <tr key={r}>
                {Array.from({ length: el.cols }).map((_, c) => {
                  const cell = (el.cells || {})[`${r}_${c}`] || {};
                  if (cell.hidden) return null;
                  return <td key={c} colSpan={cell.cs || 1} rowSpan={cell.rs || 1} style={{ border: el.border ? "1px solid #334155" : "1px solid transparent", padding: 3, fontSize: 11, verticalAlign: "top" }}>{cell.text || ""}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (el.type === "image") {
    const label = el.fieldImage ? humanize(el.fieldImage) : "Image";
    const isQr = el.fieldImage === "qr_code" || el.fieldImage === "attendance_qr";
    return (
      <div key={el.id} style={common}>
        {el.src
          ? <img src={el.src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          : isQr
            ? <img src={QR_SRC(`${window.location.origin}/verify/sample`)} alt="QR" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#e2e8f0", border: "1px dashed #94a3b8", fontSize: 9, color: "#64748b", textAlign: "center", padding: 2 }}>{label}</div>}
      </div>
    );
  }
  return null;
}

export function CanvasPreview({ canvas }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0);
  const baseW = (canvas?.size?.w || 54) * PX;
  const baseH = (canvas?.size?.h || 85.6) * PX;
  const sides = [canvas?.front || []];
  if ((canvas?.back || []).length) sides.push(canvas.back);
  const n = sides.length;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      const gap = 8;
      const availW = (el.clientWidth - gap * (n - 1)) / n;
      setScale(Math.max(0, Math.min(availW / baseW, el.clientHeight / baseH)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [baseW, baseH, n]);
  return (
    <div ref={ref} className="absolute inset-0 flex items-center justify-center gap-2 overflow-hidden bg-slate-50 p-2" data-testid="canvas-card-preview">
      {scale > 0 && sides.map((els, i) => (
        <div key={i} className="relative" style={{ width: baseW * scale, height: baseH * scale, flex: "none" }}>
          <div style={{ width: baseW, height: baseH, transform: `scale(${scale})`, transformOrigin: "top left", background: canvas?.bg || "#ffffff", position: "absolute", top: 0, left: 0, boxShadow: "0 2px 10px rgba(0,0,0,.14)", borderRadius: 6 }}>
            {els.map(renderEl)}
          </div>
          {n > 1 && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 text-[8px] font-medium text-slate-400">{i === 0 ? "Front" : "Back"}</span>}
        </div>
      ))}
    </div>
  );
}
